import {
  BYTES_POR_AMOSTRA,
  BYTES_POR_CHUNK,
  TAXA_HZ,
} from '@modules/aawz-intelligence-v2/utils/avatar-ao-vivo/protocolo'

// ★ FATIAR o PCM da frase em chunks de ~1 s — função PURA, sem WebSocket,
// sem DOM e sem `AudioContext` (spec-009).
//
// O nosso TTS devolve a frase INTEIRA em base64 (`apiVoz.sintetizar` com
// `para_avatar: true` → PCM 16-bit 24 kHz mono). O `agent.speak` quer pedaços
// de ~1 s. Entre os dois há uma só operação, e ela é aritmética de bytes —
// nenhuma decodificação de áudio acontece aqui.
//
// ⚠️ **Por que fatiar em BYTES e não em caracteres base64.** Base64 codifica
// 3 bytes em 4 caracteres: cortar a STRING em qualquer posição que não seja
// múltiplo de 4 produz um pedaço que não decodifica, e cortar em múltiplo de
// 4 produz pedaços de 3 bytes — que não são múltiplos de 2, ou seja, partem
// uma amostra de 16 bits ao meio. O avatar fala estalando e nada no fio
// acusa. Por isso: decodifica UMA vez, fatia por byte, recodifica cada chunk.

/** Quantos bytes de PCM valem `segundos` de áudio nesta taxa. */
export const bytesDeSegundos = (segundos) =>
  Math.round(segundos * TAXA_HZ * BYTES_POR_AMOSTRA)

/** Quantos segundos de áudio há em `bytes` de PCM nesta taxa. */
export const segundosDeBytes = (bytes) =>
  (bytes || 0) / (TAXA_HZ * BYTES_POR_AMOSTRA)

/**
 * `atob`/`btoa` do ambiente, isolados para o teste poder medi-los sem DOM.
 * Fora do navegador (SSR, worker sem `window`) devolvem `null` em vez de
 * lançar — quem chama trata a ausência como "não dá para falar agora".
 */
const _decodificar = (base64) => {
  if (typeof atob !== 'function') return null
  try {
    return atob(base64)
  } catch (e) {
    // Base64 inválido é dado do servidor fora do contrato: melhor devolver
    // nada e cair no retrato do que mandar lixo ao provedor.
    return null
  }
}

const _codificar = (binario) => {
  if (typeof btoa !== 'function') return null
  try {
    return btoa(binario)
  } catch (e) {
    return null
  }
}

/**
 * Fatia um PCM base64 em chunks base64 de ~1 s, alinhados por amostra.
 *
 * @param {string} pcmBase64 o áudio inteiro da frase, como o servidor mandou.
 * @param {number} [bytesPorChunk] só para teste — em produção é o do protocolo.
 * @returns {string[]} os chunks, na ordem. Lista VAZIA quando não há o que
 *   falar (entrada vazia, base64 inválido, ambiente sem `atob`/`btoa`) —
 *   nunca `null`, para o chamador poder iterar sem guarda.
 */
export const fatiarPcmEmChunks = (pcmBase64, bytesPorChunk = BYTES_POR_CHUNK) => {
  if (!pcmBase64 || typeof pcmBase64 !== 'string') return []

  const binario = _decodificar(pcmBase64)
  if (!binario) return []

  // PCM de 16 bits tem comprimento PAR por construção. Um byte solto no fim
  // só aparece se a origem estiver errada — descartá-lo é melhor que mandar
  // meia amostra, que o provedor interpreta como ruído de alta frequência.
  const tamanho = binario.length - (binario.length % BYTES_POR_AMOSTRA)
  if (tamanho <= 0) return []

  // Teto defensivo: chunk precisa caber uma amostra inteira e ser par, senão
  // TODO chunk sai desalinhado (e não só o último).
  const passo = Math.max(
    BYTES_POR_AMOSTRA,
    bytesPorChunk - (bytesPorChunk % BYTES_POR_AMOSTRA),
  )

  const chunks = []
  for (let i = 0; i < tamanho; i += passo) {
    const pedaco = _codificar(binario.slice(i, Math.min(i + passo, tamanho)))
    // Um pedaço que não codifica não pode virar buraco silencioso no meio da
    // fala: aborta a frase inteira em vez de entregá-la picotada.
    if (!pedaco) return []
    chunks.push(pedaco)
  }
  return chunks
}

/**
 * A duração, em segundos, de um PCM base64 — sem decodificar o áudio.
 *
 * Base64 tem 4 caracteres por 3 bytes, e o `=` final marca 1 ou 2 bytes de
 * enchimento. A conta abaixo é exata, e serve para dimensionar prazos sem
 * pagar o `atob` de novo.
 */
export const duracaoEmSegundos = (pcmBase64) => {
  if (!pcmBase64 || typeof pcmBase64 !== 'string') return 0
  const limpo = pcmBase64.replace(/\s/g, '')
  if (!limpo) return 0
  const enchimento = (limpo.match(/=+$/) || [''])[0].length
  const bytes = Math.max(0, (limpo.length / 4) * 3 - enchimento)
  return segundosDeBytes(bytes)
}
